import fr from '../../data/i18n/fr.json';
import en from '../../data/i18n/en.json';
import { resolve } from './i18nCore.js';
import { toEnPageSlug, toFrPageSlug, translatePagePath } from './SlugUtils.js';

export default class LanguageManager {
    /**
     * `/en` et `/en/…` sont anglais — mais pas `/energy` : le test doit être
     * explicite et non un `startsWith('/en')`, qui amputerait un slug commençant
     * par "en".
     */
    static isEnglishPathname(pathname) {
        return pathname === '/en' || pathname.startsWith('/en/');
    }

    constructor() {
        this.isEnglishPath = LanguageManager.isEnglishPathname(window.location.pathname);
        this.currentLang = this.isEnglishPath ? 'en' : 'fr';

        this.translations = { fr, en };

        this.init();
    }

    detectLanguage() {
        // Only run redirection logic if we're at the root of a language or home
        // and if it's the first time (no session flag)
        if (sessionStorage.getItem('geonerd.lang_redirected')) {
            return;
        }

        const targetLang = localStorage.getItem('geonerd.language');

        // If no preference is saved, we don't redirect (stay on current URL)
        if (!targetLang) {
            return;
        }

        sessionStorage.setItem('geonerd.lang_redirected', 'true');

        if (targetLang === 'en' && !this.isEnglishPath) {
            this.redirectToLanguage('en');
        } else if (targetLang === 'fr' && this.isEnglishPath) {
            this.redirectToLanguage('fr');
        }
    }

    redirectToLanguage(lang) {
        const path = window.location.pathname;
        const isEnglish = LanguageManager.isEnglishPathname(path);
        let newPath;

        if (lang === 'en' && !isEnglish) {
            // `/en/` et non `/en` : ce dernier répond 301, ce qui ajouterait un
            // aller-retour à chaque changement de langue depuis l'accueil.
            newPath = path === '/' ? '/en/' : '/en' + translatePagePath(path, toEnPageSlug);
        } else if (lang === 'fr' && isEnglish) {
            const stripped = path.replace(/^\/en/, '') || '/';
            newPath = stripped === '/' ? '/' : translatePagePath(stripped, toFrPageSlug);
        }

        if (newPath && newPath !== path) {
            window.location.href = newPath + window.location.search + window.location.hash;
        }
    }

    init() {
        // Unify cross-language stats before anything reads them.
        this.migrateGlobalStats();

        // Detect and redirect if necessary
        this.detectLanguage();

        // Update document language
        document.documentElement.lang = this.currentLang;

        // Update meta tags
        this.updateMetaTags();

        // Create language selector
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.createLanguageSelector();
                this.translatePage();
            });
        } else {
            // DOM is already ready
            this.createLanguageSelector();
            this.translatePage();
        }
    }

    /**
     * Slug de la page courante, préfixe de langue retiré ('' pour la home).
     * Dérivé du pathname à chaque appel plutôt que de `this.isEnglishPath` :
     * la méthode reste juste même si l'instance n'a pas été construite
     * normalement.
     */
    getCurrentSlug() {
        const path = window.location.pathname;
        const pathNoLang = LanguageManager.isEnglishPathname(path) ? path.replace(/^\/en/, '') : path;
        return pathNoLang.replace(/^\/|\/$|\.html$/g, '');
    }

    updateMetaTags() {
        const t = this.translations[this.currentLang];
        // Le préfixe /en est retiré avant de dériver la clé de page, sinon toutes
        // les pages anglaises sauf la home retomberaient sur `t.pages.home`
        // (le fallback home masque le bug pour la seule home). Le slug est aussi
        // ramené à son équivalent français canonique (`about` → `a-propos`) :
        // les clés `pages.*` sont indexées sur celui-ci, pas sur le slug anglais.
        const slug = this.getCurrentSlug() || 'home';
        const frSlug = this.currentLang === 'en' ? toFrPageSlug(slug) : slug;

        // Convert dash-case path to camelCase for translation key
        const pageKey = frSlug.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        // Pas de `|| t.pages.home` : les pages sans clé `pages.*` sont celles dont les
        // métas sont générées au build, pays par pays (`/pays/allemagne`). Retomber sur
        // la home donnait à chaque fiche le titre et la description de l'accueil, en
        // écrasant au runtime le HTML statique pourtant juste. Leur titre est déjà en
        // place : on ne touche alors qu'aux URLs canoniques plus bas.
        const pageMeta = t.pages[pageKey];

        if (pageMeta) {
            document.title = pageMeta.title;

            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
                metaDescription.content = pageMeta.description;
            }

            // Update Open Graph tags
            const ogTitle = document.querySelector('meta[property="og:title"]');
            if (ogTitle) {
                ogTitle.content = pageMeta.title;
            }

            const ogDescription = document.querySelector('meta[property="og:description"]');
            if (ogDescription) {
                ogDescription.content = pageMeta.description;
            }

            // Update Twitter tags
            const twitterTitle = document.querySelector('meta[name="twitter:title"]');
            if (twitterTitle) {
                twitterTitle.content = pageMeta.title;
            }

            const twitterDescription = document.querySelector('meta[name="twitter:description"]');
            if (twitterDescription) {
                twitterDescription.content = pageMeta.description;
            }
        }

        // Update Canonical and URL tags.
        // Le préfixe de langue porte son slash FINAL (`/en/` et non `/en`) :
        // nginx redirige `/en` en 301 vers `/en/`, et un canonical qui pointe
        // vers une redirection est un signal dégradé. Doit rester aligné sur
        // _header.html (balises statiques) et sur la tâche gulp `sitemap`.
        const baseUrl = 'https://geo-nerd.com';
        const langPrefix = this.currentLang === 'en' ? '/en/' : '/';
        const fullUrl = `${baseUrl}${langPrefix}${this.getCurrentSlug()}`;

        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) {
            canonical.href = fullUrl;
        }

        const ogUrl = document.querySelector('meta[property="og:url"]');
        if (ogUrl) {
            ogUrl.content = fullUrl;
        }

        const twitterUrl = document.querySelector('meta[property="twitter:url"]');
        if (twitterUrl) {
            twitterUrl.content = fullUrl;
        }
    }

    createLanguageSelector() {
        const header = document.querySelector('.site-header');
        if (!header) return;

        // Check if selector already exists
        if (header.querySelector('.language-selector')) return;

        const selector = document.createElement('div');
        selector.className = 'language-selector';

        const otherLangs = Object.keys(this.translations).filter(lang => lang !== this.currentLang);

        selector.innerHTML = `
			<button class="lang-toggle">${this.currentLang.toUpperCase()}</button>
			<ul class="lang-list">
				${otherLangs.map(lang => `
					<li><button class="lang-item" data-lang="${lang}">${lang.toUpperCase()}</button></li>
				`).join('')}
			</ul>
		`;

        // Posé une fois dans la barre, entre le bouton son et le bloc compte
        // (l'ordre demandé). Son déplacement dans le panneau mobile est le travail
        // de `HeaderMenu.js`, qui déplace aussi le bloc compte : deux modules qui
        // repositionnent le même élément au même point de rupture finiraient par
        // se contredire.
        const controls = header.querySelector('.header-controls');
        const account = controls && controls.querySelector('.account-menu');
        if (controls) {
            if (account) controls.insertBefore(selector, account);
            else controls.appendChild(selector);
        }

        // Toggle dropdown
        const toggle = selector.querySelector('.lang-toggle');
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            selector.classList.toggle('open');
        });

        // Close dropdown when clicking elsewhere
        document.addEventListener('click', () => {
            selector.classList.remove('open');
        });

        // Add event listeners
        selector.querySelectorAll('.lang-item').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const lang = e.target.dataset.lang;
                this.changeLanguage(lang);
            });
        });
    }

    translatePage() {
        // Translate all elements with data-i18n or data-i18n-html attribute
        document.querySelectorAll('[data-i18n], [data-i18n-html]').forEach(element => {
            const i18nKey = element.getAttribute('data-i18n');
            const i18nHtmlKey = element.getAttribute('data-i18n-html');

            // Determine which key to use and whether it's HTML
            const key = i18nKey || i18nHtmlKey;
            const isHtml = element.hasAttribute('data-i18n-html');

            if (!key || key === 'true') return; // Skip if no key or boolean-like value from attribute

            const translation = this.t(key);
            if (translation === key) return; // Skip if translation not found

            if (isHtml) {
                element.innerHTML = translation;
            } else {
                element.textContent = translation;
            }
        });

        // Translate placeholders
        document.querySelectorAll('[data-i18n-placeholder]').forEach(element => {
            const key = element.getAttribute('data-i18n-placeholder');
            const translation = this.t(key);
            if (translation !== key) {
                element.placeholder = translation;
            }
        });

        // Translate aria-labels (accessible name of icon-only buttons)
        document.querySelectorAll('[data-i18n-aria-label]').forEach(element => {
            const key = element.getAttribute('data-i18n-aria-label');
            const translation = this.t(key);
            if (translation !== key) {
                element.setAttribute('aria-label', translation);
            }
        });
    }

    changeLanguage(lang) {
        if (lang === this.currentLang) return;

        localStorage.setItem('geonerd.language', lang);
        this.redirectToLanguage(lang);
    }

    /**
     * Délégué à `i18nCore.resolve` : la tâche gulp `injectI18n` résout les
     * mêmes clés au build pour écrire le texte dans le HTML statique. Toute
     * divergence entre les deux ferait sauter le texte au chargement.
     */
    t(key) {
        return resolve(this.translations, this.currentLang, key);
    }

    /**
     * Keys whose values are language-independent and should be SHARED across
     * languages (a best score / play count is the same whether you play in FR
     * or EN). Everything else stays language-scoped because it embeds
     * language-specific data (country names, Country Nerd letter progress…).
     */
    static GLOBAL_KEY_SUFFIXES = ['.best', '.gamesPlayed', '.cumulativeScore', '.bestRun', '.weekRun', '.avatar'];

    isGlobalKey(key) {
        return LanguageManager.GLOBAL_KEY_SUFFIXES.some(suffix => key.endsWith(suffix));
    }

    getStorageKey(key) {
        if (this.isGlobalKey(key)) return key;
        return `${this.currentLang}.${key}`;
    }

    /**
     * One-shot migration: fold previously language-prefixed stats
     * (`fr.{key}` / `en.{key}` ending in a global suffix) into their shared,
     * unprefixed key — best = max across languages, counts = sum — so switching
     * language no longer hides the player's scores. Runs once, guarded by a flag.
     */
    migrateGlobalStats() {
        if (localStorage.getItem('geonerd.statsMigrated')) return;

        // Snapshot keys first since we mutate localStorage in the loop.
        const allKeys = [];
        for (let i = 0; i < localStorage.length; i++) allKeys.push(localStorage.key(i));

        const langPrefix = /^(?:fr|en)\.(.+)$/;
        for (const fullKey of allKeys) {
            const match = fullKey.match(langPrefix);
            if (!match) continue;

            const bareKey = match[1];
            if (!this.isGlobalKey(bareKey)) continue;

            const value = parseInt(localStorage.getItem(fullKey), 10) || 0;
            const existing = parseInt(localStorage.getItem(bareKey), 10) || 0;
            const merged = bareKey.endsWith('.best') ? Math.max(existing, value) : existing + value;

            localStorage.setItem(bareKey, merged.toString());
            localStorage.removeItem(fullKey);
        }

        localStorage.setItem('geonerd.statsMigrated', '1');
    }

    getCountriesDataPath() {
        return `/data/countries-${this.currentLang}.json`;
    }

    getUsStatesDataPath() {
        return `/data/us-states-${this.currentLang}.json`;
    }
}
